<i18n src="@/locales/modules/RequestTranslate.json"></i18n>

<template>
	<el-dialog append-to-body class="custom-dialog request-approver-dialog" :title="title" :visible.sync="isDialogActiveLocal">
		<div slot="title" class="flex justify-between items-center w-full">
			<span class="el-dialog__title break-normal">{{ title }}</span>
			<request-context-menu
				displayMode="detail"
				:request="value"
				@onClickCancel="requestId => $emit('cancelRequest', requestId)"
				@onClickCreateTodo="request => $emit('onClickCreateTodo', request)"
				@onClickDuplicate="requestId => $emit('onClickDuplicate', requestId)"
			/>
		</div>

		<cbs-comment-with-toggle :postId="value && value.id" :model="$constant.RELATION_MODELS.REQUEST" :key="`${value ? value.id : 'default'}-${isDialogActiveLocal}`">
			<div class="px-6">
				<el-collapse class="cbs-collapse -mx-6 my-0 mb-6" value="1">
					<el-collapse-item name="1">
						<template slot="title">
							<h6 class="uppercase font-bold ml-6 text-black">{{ $t("FormStepInfo") }}</h6>
						</template>
						<request-step-info class="m-6 mb-0" :value="requestStepGeneralInfo" />
					</el-collapse-item>
				</el-collapse>

				<request-info-approver
					v-for="(form, index) in requestFormSteps"
					class="mb-6 -mx-6 mt-0"
					:key="index"
					:value="form"
					:mappedAttachObj="mappedAttachObj"
					:title="index === 0 ? $t('Content') : form.name"
					:requestPurpose="index === 0 ? requestPurpose : null"
					:disabled="requestFormStepActiveIndex && index > requestFormStepActiveIndex"
					:isPendingInput="value.reqstatus && !!value.reqstatus.pending_input && value.reqstatus.id === form.id"
				/>
			</div>
		</cbs-comment-with-toggle>

		<div slot="footer" :class="['flex items-center flex-wrap sm:flex-no-wrap', isCanExport ? 'justify-end' : isApprovable ? 'sm:px-12 justify-between' : 'justify-center']">
			<vs-button v-if="isApprovable" color="danger" class="w-full sm:w-2/5 mb-3 sm:mb-0 sm:mr-6 last:mr-0 last:mb-0 font-bold" @click.stop="$emit('handleClickApproveOrReject', value.id, false)">
				{{ $t("Actions.Reject") }}
			</vs-button>

			<vs-button v-if="isApprovable" color="success" class="w-full sm:w-2/5 mb-3 sm:mb-0 sm:mr-6 last:mr-0 last:mb-0 font-bold" @click.stop="$emit('handleClickApproveOrReject', value.id, true)">
				{{ $t("Actions.Agree") }}
			</vs-button>

			<vs-button v-if="isEditable" class="w-full sm:w-2/5 mb-3 sm:mb-0 sm:mr-6 last:mr-0 last:mb-0 font-bold" @click.stop="$emit('handleClickEdit', value)">{{ $t("Actions.Edit") }}</vs-button>

			<cbs-download-button v-if="isCanExport" :btnText="$t('Actions.Download')" @click="handleExportData" />
		</div>
	</el-dialog>
</template>

<script>
import requestV2GQL from "@/http/requests/request/request_v2_gql";

import RequestStepInfo from "./RequestStepInfo";
import RequestContextMenu from "./RequestContextMenu";
import RequestInfoApprover from "./RequestInfoApprover";
import CbsCommentWithToggle from "@/components/cbs-comment-with-toggle/CbsCommentWithToggle";

export default {
	inheritAttrs: false,
	name: "RequestApproverDialog",
	components: {
		RequestStepInfo,
		RequestContextMenu,
		RequestInfoApprover,
		CbsCommentWithToggle
	},
	props: {
		isDialogActive: {
			type: Boolean,
			required: true
		},
		value: {
			type: Object,
			required: true
		},
		title: {
			type: String,
			required: true
		},
		isEditable: Boolean,
		isApprovable: Boolean,
		requestPurpose: String,
		mappedAttachObj: Object
	},
	emits: {
		cancelRequest: {},
		handleClickEdit: {},
		onClickDuplicate: {},
		onClickCreateTodo: {},
		handleClickApproveOrReject: {}
	},
	computed: {
		isDialogActiveLocal: {
			get() {
				return this.isDialogActive;
			},
			set(val) {
				if (!val) {
					this.$emit("closeDialog");
				}
			}
		},
		requestFormSteps() {
			return this.value.requestFormSteps || [];
		},
		requestFormStepActiveIndex() {
			const currentFormStepStatus = this.value.reqstatus;
			return this.requestFormSteps.findIndex(requestFormStep => requestFormStep.id === currentFormStepStatus.id);
		},
		requestStepGeneralInfo() {
			const requestCurrentStatus = this.value.reqstatus || {};
			let statusName = requestCurrentStatus.name;
			const status = requestCurrentStatus.status || 0;
			const requestCode = this.value.code;
			const createdDate = this.value.created_at;
			const creatorName = this.value.requester.name;
			const departmentName = this.value.requester.department.name;
			const requestType = this.value.reqtype || {};

			if (this.value.status === this.$constant.REQUEST_STATUSES.APPROVED) {
				statusName = this.$t("Complete");
			}
			if (this.value.status === this.$constant.REQUEST_STATUSES.CANCELED) {
				statusName = this.$t("Canceled");
			}
			const result = {
				status,
				statusName,
				creatorName,
				departmentName,
				createdDate,
				requestCode,
				requestTypeName: requestType.name,
				relations: this.value && this.value.relations ? this.value.relations : null,
				cancelReason: status === this.$constant.REQUEST_STATUSES.CANCELED ? this.value.cancel_reason : null
			};

			return result;
		},
		isCanExport() {
			return this.value && this.value.can_export;
		}
	},
	methods: {
		async handleExportData() {
			try {
				this.$vs.loading();
				const response = await requestV2GQL.exportRequest(this.value.id);
				const fileName = response.split("/");
				await this.handleDownloadFile({
					path: response,
					file_name: fileName[fileName.length - 1]
				});
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$nextTick(() => this.$vs.loading.close());
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.request-approver-dialog {
	::v-deep .el-collapse-item__content {
		padding-bottom: 0;
	}
}
</style>
